// Offene Dose im mobilen 2x2-Sorten-Raster: markiert die Karte unter dem
// Finger.
//
// Diese Datei setzt nur den Zustand, sie zeigt nichts an. Daran haengt das
// Standbild der geoeffneten Dose (::after-Ebene in assets/lucid-brand.css):
// `lucid-is-open` ist das Touch-Pendant zum Hover. Der Finger tritt an die
// Stelle des Zeigers — die Dose, auf der er liegt, ist offen, beim Tippen,
// beim Halten und waehrend der Finger beim Scrollen darueber liegt.
//
// Das VIDEO startet daneben assets/lucid-can-video.js direkt im touchstart
// (Sesh-Muster, siehe dort). Das Standbild hier ist die Rueckfallebene
// darunter — laeuft das Video, deckt es sie ab.
//
// Warum die Klasse und nicht schlicht :active: WebKit zieht :active zurueck,
// sobald es die Beruehrung als Scrollen uebernimmt, und stellt ab da auch
// touchmove ein. Der Zustand soll aber halten, solange der Finger liegt.
//
// Deshalb haengen die Listener direkt an den Karten und nicht am Container:
// touchstart auf der Karte ist eindeutig, ohne Trefferrechnung ueber
// Koordinaten.
//
// Der Dateiname sagt noch "scroller": der Container hiess bis 2026-08-03
// lucid-mobile-scroller (horizontales Karussell) und heisst als CSS-Klasse
// immer noch so, weil templates/index.json daran haengt.

(() => {
  // Dieselbe Grenze wie die Raster-Regeln in lucid-brand.css. Darueber
  // liegen die Karten in einer Reihe, dann ist der Hover zustaendig.
  const mobile = window.matchMedia('screen and (max-width: 749px)');

  // Ein Tipp dauert oft keine 100ms. Ohne Mindestdauer waere die Dose bei der
  // 260ms-Blende nie richtig zu sehen — sie wuerde zurueckblenden, bevor sie
  // ueberhaupt da war. 420ms ist der abgestimmte Wert aus der urspruenglichen
  // Fassung; die zwischenzeitlichen 900ms galten der Ladezeit des mobilen
  // Videos, das es nicht mehr gibt.
  const MIN_OPEN_MS = 420;

  const bind = (scroller) => {
    // Der Theme-Editor rendert Sections neu; ohne die Markierung haengten nach
    // jedem Rerender mehrere Listener am selben Element.
    if (scroller.dataset.lucidScrollerOpen === 'on') return;
    scroller.dataset.lucidScrollerOpen = 'on';

    const cards = [...scroller.children];
    if (cards.length < 2) return;

    let open = null;
    let openedAt = 0;
    let closeTimer = 0;

    const apply = (card) => {
      if (card === open) return;
      if (open) open.classList.remove('lucid-is-open');
      if (card) card.classList.add('lucid-is-open');
      open = card;
    };

    const press = (card) => {
      clearTimeout(closeTimer);
      openedAt = Date.now();
      apply(card);
    };

    const release = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => apply(null), Math.max(0, MIN_OPEN_MS - (Date.now() - openedAt)));
    };

    for (const card of cards) {
      card.addEventListener(
        'touchstart',
        () => {
          if (mobile.matches) press(card);
        },
        { passive: true }
      );
    }

    // Das Ende der Geste kommt vom document, nicht von der Karte: scrollt die
    // Seite unter dem Finger weiter, stellt WebKit die Zustellung an das
    // urspruengliche Ziel nicht garantiert durch. `touches.length` ist das
    // verlaessliche Kriterium — erst wenn wirklich kein Finger mehr auf dem
    // Glas liegt, geht die Dose zu.
    const endIfDone = (event) => {
      if (event.touches && event.touches.length > 0) return;
      release();
    };

    document.addEventListener('touchend', endIfDone, { passive: true });
    document.addEventListener('touchcancel', endIfDone, { passive: true });

    // Die geoeffnete Dose als Standbild vorladen, sobald das Raster in die
    // Naehe des Sichtfensters kommt. Das sind vier WebP-Aufnahmen zu je ~23 KB
    // und nur fuer den, der so weit scrollt. Genau dieses Vorladen ist der
    // Grund, warum sich der Griff auf eine Dose nie tot anfuehlt: das
    // Standbild ist beim ersten Aufsetzen schon da.
    const preload = (card) => {
      if (card.dataset.lucidCan) return;
      card.dataset.lucidCan = 'loading';

      const block = card.querySelector('.image-block');
      const value = block ? getComputedStyle(block).getPropertyValue('--lucid-can-open').trim() : '';
      // Das Ent-Escaping am Ende: Safari serialisiert Custom Properties mit
      // Backslashes (url(\/cdn\/...\.png)) — ohne den Schritt lief dieses
      // Vorladen auf iOS ins Leere. Details im urlFrom von lucid-can-video.js.
      const url = value
        .replace(/^url\(\s*["']?/, '')
        .replace(/["']?\s*\)$/, '')
        .replace(/\\(.)/g, '$1');

      if (!url) {
        delete card.dataset.lucidCan;
        return;
      }

      const image = new Image();
      image.addEventListener('load', () => {
        card.dataset.lucidCan = 'ready';
      });
      image.src = url;
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          // Erst abmelden, wenn wirklich vorgeladen wurde. Andersherum (so
          // stand es hier) verliert ein Geraet, das die Kacheln zuerst breit
          // sieht, das Vorladen fuer immer — Drehen ins Hochformat oder ein
          // schmaler gezogenes Fenster kaeme dann nie mehr dazu.
          if (!mobile.matches) return;
          observer.disconnect();
          cards.forEach(preload);
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(scroller);
    } else {
      for (const card of cards) {
        card.addEventListener('touchstart', () => cards.forEach(preload), { passive: true, once: true });
      }
    }
  };

  const bindAll = (scope) => {
    for (const scroller of scope.querySelectorAll('.lucid-mobile-scroller > .group-block-content')) {
      bind(scroller);
    }
  };

  bindAll(document);
  document.addEventListener('shopify:section:load', (event) => bindAll(event.target));
})();
